/**
 * Solitaire — hub at /solitaire.
 *
 * A variant picker on the card table. Each variant gets a card carrying a
 * small fanned-cards motif and TWO direct affordances — "Deal of the Day"
 * (today's seed, the same for everyone in a community) and "New deal" (a fresh
 * reseed for unlimited play). Both route STRAIGHT to the table; there is no
 * intermediate screen between the hub and play (BUILDER-PLAYBOOK rule #5).
 *
 * Variants beyond Klondike + FreeCell appear automatically as the engines
 * register them (we read the live `VARIANTS` list), shown as "coming soon"
 * until they have a blurb here.
 */
import { motion } from 'motion/react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { CardFace, makeDeck } from '@/cards';
import { usePreferences } from '@/preferences';

import { Table } from './Table';
import { cardSpring, hoverSpring, tableSpring } from './motion';
import { sound } from './sound';
import { VARIANTS } from './variants';

import '@/cards/cards.css';
import './SolitaireHub.css';

interface VariantBlurb {
  eyebrow: string;
  blurb: string;
  /** card ids for the little fanned motif (face-up). */
  motif: string[];
}

const BLURBS: Record<string, VariantBlurb> = {
  klondike: {
    eyebrow: 'The classic',
    blurb: 'Build the four suits up from Ace to King. Draw one card at a time — unhurried, the way most people learned it.',
    motif: ['SA', 'H2', 'S3', 'D4'],
  },
  freecell: {
    eyebrow: 'Every deal is winnable',
    blurb: 'All the cards face-up from the start. Four free cells to park a card while you think. Pure skill, no luck.',
    motif: ['CA', 'D2', 'C3', 'H4'],
  },
};

export function SolitaireHub(): React.JSX.Element {
  const navigate = useNavigate();
  const { theme } = usePreferences();

  useEffect(() => {
    const t = window.setTimeout(() => {
      sound.unlock();
      sound.play('nav');
    }, 80);
    return () => window.clearTimeout(t);
  }, []);

  const go = (variantId: string, mode: 'daily' | 'practice'): void => {
    sound.unlock();
    sound.play('nav');
    navigate(`/play/solitaire?variant=${variantId}&mode=${mode}`);
  };

  const deckById = motifLookup();

  return (
    <div className="sol-hub" data-theme={theme}>
      <Table>
        <motion.header
          className="sol-hub__head"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...tableSpring, delay: 0.05 }}
        >
          <p className="sol-hub__eyebrow">Rendever Games</p>
          <h1 className="sol-hub__title">Solitaire</h1>
          <p className="sol-hub__lede">
            A quiet table for one. Pick a game, and play today&rsquo;s deal — the
            same one everyone in your community is playing — or deal a fresh one
            whenever you like.
          </p>
        </motion.header>

        <section className="sol-hub__choices" aria-label="Choose a game">
          <ul className="sol-hub__cards" role="list">
            {VARIANTS.map((v, i) => {
              const blurb = BLURBS[v.id];
              return (
                <motion.li
                  key={v.id}
                  className="sol-hub__card-item"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ ...cardSpring, delay: 0.14 + i * 0.08 }}
                  whileHover={{ y: -4, transition: hoverSpring }}
                >
                  <article className="sol-hub__card" data-variant={v.id}>
                    <div className="sol-hub__card-top">
                      <span className="sol-hub__card-eyebrow">
                        {blurb?.eyebrow ?? 'Coming soon'}
                      </span>
                      <h2 className="sol-hub__card-title">{v.name}</h2>
                    </div>

                    <div className="sol-hub__motif" aria-hidden="true">
                      {(blurb?.motif ?? ['SA', 'HA', 'DA', 'CA']).map((id, mi) => {
                        const card = deckById.get(id);
                        if (!card) return null;
                        return (
                          <span
                            key={id}
                            className="sol-hub__motif-card"
                            style={{ ['--i' as string]: mi - 1.5 }}
                          >
                            <CardFace card={card} />
                          </span>
                        );
                      })}
                    </div>

                    <p className="sol-hub__card-blurb">{blurb?.blurb ?? 'On its way.'}</p>

                    <div className="sol-hub__card-actions">
                      <button
                        type="button"
                        className="sol-hub__btn sol-hub__btn--primary"
                        onClick={() => go(v.id, 'daily')}
                        onPointerEnter={() => sound.play('lift')}
                      >
                        Deal of the Day
                      </button>
                      <button
                        type="button"
                        className="sol-hub__btn sol-hub__btn--secondary"
                        onClick={() => go(v.id, 'practice')}
                        onPointerEnter={() => sound.play('lift')}
                      >
                        New deal
                      </button>
                    </div>
                  </article>
                </motion.li>
              );
            })}
          </ul>
        </section>
      </Table>
    </div>
  );
}

function motifLookup() {
  const m = new Map<string, ReturnType<typeof makeDeck>[number]>();
  for (const c of makeDeck()) m.set(c.id, { ...c, faceUp: true });
  return m;
}
